<template>
  <div class="calm qp">
    <!-- ═══════════════ HEADER ═══════════════ -->
    <header class="calm__header">
      <div class="calm__header__row">
        <div>
          <div class="c-eyebrow">Quick Payment</div>
          <h1 class="calm__header__title">รับชำระเงินที่สาขา</h1>
          <div class="calm__header__sub">
            <span><strong class="c-mono">{{ cart.length }}</strong> รายการในตะกร้า</span>
            <span class="calm__header__sep">·</span>
            <span>รวม <strong class="c-mono">{{ formatBaht(totalPay) }}</strong> บาท</span>
            <span class="calm__header__sep">·</span>
            <span class="c-mono c-mute">วันนี้ {{ todayStamp }}</span>
          </div>
        </div>
      </div>
    </header>

    <div class="calm__body">
      <!-- ═══════════════ ค้นหา / เพิ่มกรมธรรม์ ═══════════════ -->
      <div class="calm__card">
        <div class="calm__card__head">
          <div>
            <div class="calm__card__title">เพิ่มกรมธรรม์</div>
            <div class="calm__card__hint">พิมพ์เลขกรมธรรม์แล้วกด Enter — เพิ่มได้หลายเลข</div>
          </div>
        </div>
        <div class="qp-add">
          <input
            v-model="policyInput"
            class="qp-input"
            type="text"
            placeholder="เลขกรมธรรม์ เช่น 24-AC1-163-2023-01"
            @keyup.enter="addPolicy"
          />
          <button class="c-btn c-btn--primary" :disabled="isLooking || !policyInput" @click="addPolicy">
            <i class="fas fa-plus mr-1"></i> เพิ่ม
          </button>
        </div>
      </div>

      <!-- ═══════════════ ตะกร้ารายการ ═══════════════ -->
      <div class="calm__card calm__card--flush">
        <div class="qp-table-head">
          <div class="calm__card__title">รายการที่จะชำระ</div>
          <div class="qp-total">รวมทั้งสิ้น <strong class="c-mono">{{ formatBaht(totalPay) }}</strong> บาท</div>
        </div>
        <div class="calm__table-wrap">
          <table class="calm__table">
            <thead>
              <tr>
                <th>เลขกรมธรรม์</th>
                <th>ลูกค้า</th>
                <th>ตัวแทน</th>
                <th>ครบกำหนด</th>
                <th class="ta-r">เบี้ยรวม / ยอดรวมชำระ</th>
                <th class="ta-c">วิธีจ่ายของตัวแทน</th>
                <th class="ta-r">{{ $t("lmg.commission_amount") }}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="cart.length === 0">
                <td colspan="8" class="calm__empty">ยังไม่มีรายการ — เพิ่มเลขกรมธรรม์ด้านบน</td>
              </tr>
              <tr v-for="it in cart" :key="it.id">
                <td class="c-mono">{{ it.policy_number }}</td>
                <td>{{ it.customer_fname }} {{ it.customer_lname }}</td>
                <td class="c-soft">{{ it.owner_fname }} {{ it.owner_lname }}</td>
                <td class="c-soft">{{ it.DueDateShow }}</td>
                <td class="ta-r c-mono">
                  <div>เบี้ยรวม {{ formatBaht(it.amount_all) }}</div>
                  <div class="c-soft" style="font-size:12px">ยอดรวมชำระ {{ formatBaht(it.pay_amount) }}</div>
                </td>
                <td class="ta-c">
                  <!-- จ่ายเต็ม = ตัวแทนโอนเบี้ยรวม (สาขาติดค้างคอมฯ) → commission_deducted=0
                       หักคอมฯ = ตัวแทนหักคอมฯ แล้วโอนยอดรวมชำระ → commission_deducted=1 -->
                  <div class="qp-basis">
                    <button type="button" class="qp-basis__btn" :class="{ 'qp-basis__btn--on': !commissionDeducted[it.id] }" @click="$set(commissionDeducted, it.id, false)">จ่ายเต็ม</button>
                    <button type="button" class="qp-basis__btn" :class="{ 'qp-basis__btn--on': commissionDeducted[it.id] }" @click="$set(commissionDeducted, it.id, true)">หักคอมฯ</button>
                  </div>
                </td>
                <td class="ta-r c-mono c-soft">
                  {{ it.commission_amount != null ? formatBaht(it.commission_amount) : "-" }}
                </td>
                <td class="ta-r">
                  <button class="qp-x" title="นำออก" @click="removeItem(it.id)">
                    <i class="fas fa-times"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ═══════════════ การชำระเงิน ═══════════════ -->
      <div class="calm__card">
        <div class="calm__card__head">
          <div class="calm__card__title">วิธีชำระเงิน</div>
        </div>
        <div class="calm__filter-grid">
          <div class="fld">
            <label>{{ $t("lmg.account_select") }}</label>
            <el-select v-model="accountID" :placeholder="$t('lmg.account_select')">
              <el-option
                v-for="a in forPaymentAccounts"
                :key="a.ID"
                :label="accountLabel(a)"
                :value="a.ID"
              ></el-option>
            </el-select>
          </div>
          <div class="fld">
            <label>วันที่รับเงินจริง</label>
            <input v-model="paidDate" type="date" class="qp-input" />
          </div>
          <div class="fld" v-if="payslipRequired">
            <label>หลักฐานการชำระ (payslip)<span class="qp-req">*</span></label>
            <file-input multiple :initial-label="$t('lmg.add_files')" @change="filesChange"></file-input>
            <ul v-if="payslipFiles.length" class="qp-files">
              <li v-for="(f, i) in payslipFiles" :key="i">
                <span class="qp-files__name">{{ f.name }}</span>
                <button type="button" class="qp-files__rm" :title="$t('lmg.remove_file')" @click="removePayslipFile(i)">×</button>
              </li>
            </ul>
          </div>
          <div class="fld">
            <label>&nbsp;</label>
            <label class="qp-check">
              <input type="checkbox" v-model="useQR" /> สร้าง QR (PromptPay)
            </label>
          </div>
          <div class="fld" v-if="useQR">
            <label>บัญชี PromptPay</label>
            <el-select
              v-if="promptpay.length"
              v-model="qrBankID"
              placeholder="บัญชี PromptPay"
            >
              <el-option
                v-for="p in promptpay"
                :key="p.ID"
                :label="promptpayLabel(p)"
                :value="p.ID"
              ></el-option>
            </el-select>
            <div v-else class="qp-pp-hint">
              ยังไม่มีบัญชี PromptPay — ตั้งค่าที่เมนูธนาคาร
            </div>
          </div>
        </div>

        <div class="qp-actions">
          <button class="c-btn c-btn--primary qp-pay" :disabled="submitting || cart.length === 0" @click="submit">
            <i class="fas fa-check-circle mr-1"></i>
            {{ submitting ? "กำลังบันทึก..." : ("ยืนยันรับชำระ " + formatBaht(totalPay) + " บาท") }}
          </button>
          <button v-if="useQR" class="c-btn" :disabled="cart.length === 0" @click="submit">
            <i class="fas fa-qrcode mr-1"></i> สร้าง QR
          </button>
        </div>
      </div>
    </div>

    <!-- ═══════════════ QR Modal ═══════════════ -->
    <div v-if="showQR" class="qp-modal" @click.self="showQR = false">
      <div class="calm qp-modal__box">
        <h3 class="calm__card__title">สแกนเพื่อชำระเงิน</h3>
        <div class="qp-qr-amount c-mono">{{ formatBaht(result ? result.PayAmount : totalPay) }} บาท</div>
        <img v-if="qrDataUrl" :src="qrDataUrl" class="qp-qr-img" alt="PromptPay QR" />
        <div class="c-mute qp-qr-ref">อ้างอิง: {{ result ? result.InvoiceNumber : "" }}</div>
        <div class="qp-modal__actions">
          <button class="c-btn" @click="printReceipt">พิมพ์ใบเสร็จ</button>
          <button class="c-btn c-btn--primary" @click="finish">เสร็จสิ้น</button>
        </div>
      </div>
    </div>

    <!-- ═══════════════ ใบเสร็จ (ซ่อน แสดงตอนพิมพ์) ═══════════════ -->
    <div v-if="result" id="qp-receipt" class="qp-receipt">
      <div class="qp-receipt__title">ใบเสร็จรับเงิน / Quick Payment</div>
      <div class="qp-receipt__meta">
        เลขที่: {{ result.InvoiceNumber }}<br />
        วันที่รับเงิน: {{ paidDate }}<br />
        บัญชีรับเงิน: {{ selectedAccount ? selectedAccount.Name : "" }}
      </div>
      <table class="qp-receipt__table">
        <thead><tr><th>กรมธรรม์</th><th class="ta-r">ยอด</th></tr></thead>
        <tbody>
          <tr v-for="it in cartSnapshot" :key="it.id">
            <td>{{ it.policy_number }}</td>
            <td class="ta-r">{{ formatBaht(it.pay_amount) }}</td>
          </tr>
        </tbody>
        <tfoot><tr><td>รวม</td><td class="ta-r">{{ formatBaht(result.PayAmount) }} บาท</td></tr></tfoot>
      </table>
    </div>
  </div>
</template>

<script>
import { Select, Option } from "element-ui";
import { mapGetters, mapActions } from "vuex";
import QRCode from "qrcode";
import swal from "sweetalert2";
import FileInput from "@/components/Inputs/FileInput";

export default {
  components: {
    [Select.name]: Select,
    [Option.name]: Option,
    FileInput,
  },
  data() {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    return {
      policyInput: "",
      cart: [],
      cartSnapshot: [],
      accountID: "", // selected receiving account (from /api/bank/for-payment)
      useQR: false, // request PromptPay QR instead of plain receipt
      qrBankID: "", // selected PromptPay account the QR pays into (id from /api/promptpay-account)
      commissionDeducted: {}, // { [billingId]: bool } — agent deducted commission before transfer
      paidDate: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`,
      payslipFiles: [],
      submitting: false,
      result: null,
      qrDataUrl: "",
      showQR: false,
    };
  },
  computed: {
    ...mapGetters({
      banks: "Bank/getData",
      forPaymentAccounts: "Bank/getForPayment",
      paymentMethods: "QuickPay/getPaymentMethod",
      promptpay: "QuickPay/getPromptPay",
      isLooking: "QuickPay/isLooking",
    }),
    totalPay() {
      return this.cart.reduce((s, it) => s + (Number(it.pay_amount) || 0), 0);
    },
    todayStamp() {
      return this.paidDate;
    },
    selectedAccount() {
      return this.forPaymentAccounts.find((a) => a.ID === this.accountID) || null;
    },
    // PaymentVia derived from AccountType for BE legacy contract:
    // type 3 (branch cash) -> 1 (cash); type 1 (HQ) / type 2 (branch transfer) -> 3 (transfer)
    paymentVia() {
      const acc = this.selectedAccount;
      if (!acc) return 1;
      return Number(acc.AccountType) === 3 ? 1 : 3;
    },
    // Transfer (PaymentVia=3) needs a payslip; branch-cash (type 3) does not.
    payslipRequired() {
      return this.paymentVia === 3;
    },
  },
  async mounted() {
    this.forPaymentData();
    await this.loadPromptPay();
    // Default-select the first PromptPay account so the common case needs no extra click.
    if (!this.qrBankID && this.promptpay.length) {
      this.qrBankID = this.promptpay[0].ID;
    }
  },
  methods: {
    ...mapActions({
      lookup: "QuickPay/lookup",
      createPayment: "QuickPay/createPayment",
      requestQR: "QuickPay/requestQR",
      forPaymentData: "Bank/forPaymentData",
      loadPromptPay: "QuickPay/loadPromptPay",
    }),
    formatBaht(v) {
      return Number(v || 0).toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    methodName(id) {
      const m = this.paymentMethods.find((x) => x.ID === id);
      return m ? m.Name : "";
    },
    accountTypeLabel(type) {
      if (Number(type) === 1) return this.$t("lmg.account_type_hq");
      if (Number(type) === 2) return this.$t("lmg.account_type_branch_transfer");
      if (Number(type) === 3) return this.$t("lmg.account_type_branch_cash");
      return "";
    },
    accountLabel(a) {
      const num = a.AccountNumber ? " " + a.AccountNumber : "";
      const type = this.accountTypeLabel(a.AccountType);
      return `${a.Name}${num}${type ? " (" + type + ")" : ""}`;
    },
    // Label for a PromptPay account: name + the promptpay id (fallback to account number).
    promptpayLabel(p) {
      const ref = p.PromptpayId || p.AccountNumber || "";
      return ref ? `${p.Name} (${ref})` : p.Name;
    },
    async addPolicy() {
      const policy = this.policyInput.trim();
      if (!policy) return;
      const { items, invoiceRef } = await this.lookup(policy);
      if (items && items.length) {
        let added = 0;
        items.forEach((it) => {
          if (!this.cart.some((c) => c.id === it.id)) {
            this.cart.push(it);
            // initialise per-billing commission flag (Vue 2 reactive set)
            this.$set(this.commissionDeducted, it.id, false);
            added++;
          }
        });
        if (added === 0) swal.fire({ title: "รายการนี้อยู่ในตะกร้าแล้ว", icon: "info" });
      } else if (invoiceRef && invoiceRef.AlreadyInvoiced) {
        // Policy is already on an invoice — show which one + offer to jump there.
        const statusLabel = { 1: "รอชำระ", 2: "ชำระแล้ว", 3: "ยกเลิก" }[invoiceRef.InvoiceStatus] || "-";
        const r = await swal.fire({
          title: "กรมธรรม์อยู่ในใบวางบิลแล้ว",
          html: `กรมธรรม์ <b>${policy}</b><br>อยู่ในใบวางบิล <b>${invoiceRef.InvoiceNumber}</b> (สถานะ: ${statusLabel})`,
          icon: "info",
          showCancelButton: true,
          confirmButtonText: "ไปที่ใบวางบิล",
          cancelButtonText: "ปิด",
          buttonsStyling: false,
          confirmButtonClass: "btn btn-primary btn-fill",
          cancelButtonClass: "btn btn-secondary btn-fill",
        });
        if (r.value) {
          this.$router.push({ path: "/lmg/v2/invoice", query: { invoiceNumber: invoiceRef.InvoiceNumber } });
        }
      } else {
        swal.fire({ title: "ไม่พบรายการค้างชำระ", text: policy, icon: "warning" });
      }
      this.policyInput = "";
    },
    removeItem(id) {
      this.cart = this.cart.filter((it) => it.id !== id);
      this.$delete(this.commissionDeducted, id);
    },
    // Multi-file picker: validate (≤5 files, ≤5MB each, pdf/image only) and
    // append the accepted files to payslipFiles (keeps any already chosen).
    filesChange(files) {
      const MAX = 5;
      const MAX_SIZE = 5 * 1024 * 1024;
      const incoming = Array.from(files || []);
      for (const f of incoming) {
        const okType = f.type === "application/pdf" || /^image\//.test(f.type);
        if (!okType) {
          swal.fire({ title: this.$t("lmg.file_type_invalid"), text: f.name, icon: "error" });
          continue;
        }
        if (f.size > MAX_SIZE) {
          swal.fire({ title: this.$t("lmg.file_too_large"), text: f.name, icon: "error" });
          continue;
        }
        if (this.payslipFiles.length >= MAX) {
          swal.fire({ title: this.$t("lmg.max_files_5"), icon: "error" });
          break;
        }
        this.payslipFiles.push(f);
      }
    },
    removePayslipFile(i) {
      this.payslipFiles.splice(i, 1);
    },
    async submit() {
      if (this.cart.length === 0) return;
      if (!this.accountID) {
        swal.fire({ title: "เลือกบัญชีรับเงิน", text: this.$t("lmg.account_select"), icon: "error" });
        return;
      }
      if (this.payslipRequired && this.payslipFiles.length === 0) {
        swal.fire({ title: "ต้องแนบหลักฐานการชำระ", text: "วิธีชำระแบบโอนต้องแนบ payslip", icon: "error" });
        return;
      }
      if (this.useQR && !this.qrBankID) {
        swal.fire({ title: "กรุณาเลือกบัญชี PromptPay", icon: "error" });
        return;
      }
      this.submitting = true;
      try {
        // per-billing commission_deducted map: { "<billingId>": 0|1 }
        const commission = {};
        this.cart.forEach((it) => {
          commission[it.id] = this.commissionDeducted[it.id] ? 1 : 0;
        });

        const fd = new FormData();
        fd.append("BillingList", JSON.stringify({ BillingList: this.cart.map((it) => it.id), Note: "" }));
        fd.append("PaymentVia", this.paymentVia);
        fd.append("PaymentBankID", this.accountID || 0);
        fd.append("commission", JSON.stringify(commission));
        fd.append("PaidDate", this.paidDate);
        // Multi-file: append each under the same field name so BE reads []FileHeader.
        this.payslipFiles.forEach((f) => fd.append("Payslip", f));

        const res = await this.createPayment(fd);
        const datum = res.data.Datum || {};
        this.result = {
          InvoiceID: datum.InvoiceID,
          InvoiceNumber: datum.InvoiceNumber,
          PayAmount: datum.PayAmount != null ? datum.PayAmount : this.totalPay,
        };
        this.cartSnapshot = this.cart.slice();

        if (this.useQR && this.result.InvoiceID) {
          await this.genQR(this.result.InvoiceID);
          this.showQR = true;
        } else {
          swal.fire({
            title: "รับชำระสำเร็จ",
            html: `ใบวางบิล <b>${this.result.InvoiceNumber}</b><br/>ยอด ${this.formatBaht(this.result.PayAmount)} บาท`,
            icon: "success",
            confirmButtonText: "พิมพ์ใบเสร็จ",
            showCancelButton: true,
            cancelButtonText: "เสร็จสิ้น",
          }).then((r) => {
            if (r.value) this.printReceipt();
            else this.finish();
          });
        }
      } catch (err) {
        const msg = err && err.response && err.response.data ? err.response.data.Cause || err.response.data.Msg : "บันทึกไม่สำเร็จ";
        swal.fire({ title: "ผิดพลาด", text: msg, icon: "error" });
      } finally {
        this.submitting = false;
      }
    },
    async genQR(invoiceID) {
      try {
        const res = await this.requestQR({ InvoiceID: invoiceID, BankID: this.qrBankID });
        const payload = (res.data.Datum || {}).QRPayload;
        if (payload) {
          this.qrDataUrl = await QRCode.toDataURL(payload, { width: 280, margin: 1 });
        }
      } catch (err) {
        swal.fire({ title: "สร้าง QR ไม่สำเร็จ", icon: "error" });
      }
    },
    printReceipt() {
      window.print();
    },
    finish() {
      this.showQR = false;
      this.cart = [];
      this.cartSnapshot = [];
      this.result = null;
      this.qrDataUrl = "";
      this.payslipFiles = [];
    },
  },
};
</script>

<style scoped>
.qp .ta-r { text-align: right; }
.qp-add { display: flex; gap: 10px; align-items: center; }
.qp-input {
  flex: 1;
  height: 40px;
  padding: 0 12px;
  border: 1px solid var(--c-border-strong, #c7d1de);
  border-radius: 8px;
  background: var(--c-surface, #fff);
  color: var(--c-text, #2c3e50);
  font-size: 15px;
}
.qp-input:focus { outline: none; border-color: var(--c-blue, #3b6ea8); }
.qp-table-head {
  display: flex; justify-content: space-between; align-items: center;
  padding: 16px 20px; border-bottom: 1px solid var(--c-border, #e4e9f1);
}
.qp-total { color: var(--c-text-soft, #5e6c80); }
.qp-x {
  border: none; background: transparent; color: var(--c-text-mute, #8a96a8);
  cursor: pointer; padding: 4px 8px; border-radius: 6px;
}
.qp-x:hover { background: var(--c-tint, #eef3fa); color: #c0392b; }

/* segmented จ่ายเต็ม / หักคอมฯ toggle */
.qp-basis { display: inline-flex; border: 1px solid var(--c-border, #d8e0ea); border-radius: 7px; overflow: hidden; }
.qp-basis__btn {
  border: none; background: #fff; color: var(--c-text-mute, #7b8794);
  font: inherit; font-size: 12.5px; padding: 3px 10px; cursor: pointer; transition: background .12s, color .12s;
}
.qp-basis__btn + .qp-basis__btn { border-left: 1px solid var(--c-border, #d8e0ea); }
.qp-basis__btn--on { background: var(--c-primary, #3b6ea8); color: #fff; }
.qp-req { color: #c0392b; margin-left: 2px; }
.qp-pp-hint { color: var(--c-text-mute, #8a96a8); font-size: 13px; padding: 8px 0; }
.qp-files { list-style: none; margin: 8px 0 0; padding: 0; }
.qp-files li { display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 4px 8px; border: 1px solid var(--c-line, #e1e6ed); border-radius: 6px; margin-bottom: 4px; font-size: 13px; }
.qp-files__name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.qp-files__rm { border: none; background: transparent; color: #c0392b; font-size: 18px; line-height: 1; cursor: pointer; padding: 0 4px; }
.qp-actions { display: flex; gap: 12px; margin-top: 18px; }
.qp-pay { font-size: 16px; padding: 12px 22px; }

/* QR modal */
.qp-modal {
  position: fixed; inset: 0; background: rgba(20, 30, 45, 0.45);
  display: flex; align-items: center; justify-content: center; z-index: 2000;
}
.qp-modal__box {
  background: var(--c-surface, #fff); border-radius: 14px; padding: 26px 30px;
  text-align: center; max-width: 360px; box-shadow: 0 12px 40px rgba(0,0,0,.2);
}
.qp-qr-amount { font-size: 26px; font-weight: 700; margin: 8px 0; color: var(--c-text, #2c3e50); }
.qp-qr-img { width: 280px; height: 280px; }
.qp-qr-ref { margin-top: 6px; font-size: 13px; }
.qp-modal__actions { display: flex; gap: 10px; margin-top: 18px; justify-content: center; }

/* Receipt — hidden on screen, shown on print */
.qp-receipt { display: none; }
@media print {
  body * { visibility: hidden; }
  #qp-receipt, #qp-receipt * { visibility: visible; }
  #qp-receipt {
    display: block; position: absolute; left: 0; top: 0; width: 100%;
    padding: 16px; font-family: "TH Sarabun New", sans-serif; color: #000;
  }
  .qp-receipt__title { font-size: 20px; font-weight: 700; text-align: center; margin-bottom: 10px; }
  .qp-receipt__meta { font-size: 14px; margin-bottom: 10px; }
  .qp-receipt__table { width: 100%; border-collapse: collapse; font-size: 14px; }
  .qp-receipt__table th, .qp-receipt__table td { border-bottom: 1px solid #ccc; padding: 4px 6px; text-align: left; }
  .qp-receipt__table .ta-r { text-align: right; }
  /* 80mm receipt printer */
  @page { size: 80mm auto; margin: 4mm; }
}
</style>
